import to from 'await-to-js'
import { TextInput } from 'components/FormUtils/TextInput'
import {
  addDays,
  endOfQuarter,
  endOfYear,
  startOfQuarter,
  startOfYear,
  isBefore,
  endOfToday,
} from 'date-fns'
import { Calendar, CalendarRange, CalendarDays } from 'lucide-react'
import {
  Fragment,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { z } from 'zod'
import FbButton from '../../components/FbUI/FbButton'
import { DateRangePicker } from '../../components/FormUtils/DateRangePicker'
import { RadioButtonSelect } from '../../components/FormUtils/RadioButtonSelect'
import { Modal } from '../../components/UI/Modal/Modal'
import apiService from '../../services/api'
import Select from 'components/FormUtils/Select'
import { toast } from 'react-toastify'
import { queryClient } from '../../services/queryClient'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { Checkbox } from '../../components/FormUtils/Checkbox'
import { REBATE_OFFERS_TABLE_KEY } from './RebateOffersTable'
import { OfferPage } from '../../components/offer-page/offer-page'
import { Tooltip } from '../../components/Tooltip/tooltip'
import { useAuth } from 'context/authentication/useAuth'
import { FaInfoCircle } from 'react-icons/fa'
import {
  MobilePreview,
  VIEWPORT_PRESETS,
  ViewportPreset,
} from '../../components/MobilePreview'
import {
  CreateRebateOfferBody,
  CreateRebateOfferTermsProduct,
} from './tradespend_api'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { API_HOST } from 'utils/http-client'
import {
  ProductVariantSelector,
  ProductWithVariants,
} from 'components/UI/ProductVariantSelector'
import {
  mapVariantToDisplay,
  buildSelectedVariantsRecord,
  findProductById,
} from '../../utils/productHelpers'

enum RebateOfferType {
  TOTAL_VALUE = 'TOTAL_VALUE',
  SINGLE_CASE = 'SINGLE_CASE',
  PRICE_PER_CASE = 'PRICE_PER_CASE',
  MINIMUM_ORDER = 'MINIMUM_ORDER',
  // Legacy types kept for backwards compatibility
  FIRST_OFFER = 'FIRST_OFFER',
  TIME_OFFER = 'TIME_OFFER',
}

enum RebateBranding {
  COMPANY = 'COMPANY',
  COMPANY_PRODUCTS = 'COMPANY_PRODUCTS',
  SELECT_COMPANY_PRODUCTS = 'SELECT_COMPANY_PRODUCTS',
  PRODUCT = 'PRODUCT',
  PRODUCT_FROM_COMPANY = 'PRODUCT_FROM_COMPANY',
}

interface PeriodOption {
  label: string
  value: string
  dates: {
    from: Date
    to: Date
  }
  isDisabled: boolean
}

interface PeriodOptions {
  [key: string]: {
    quarters: PeriodOption[]
    halves: PeriodOption[]
  }
}

interface CreateRebateOfferModalProps {
  campaignId?: number
  onSuccess?: () => void
}

const createRebateOfferFormSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    type: z.string().min(1),
    branding: z.string().min(1),
    basis: z.string().min(1),
    min: z.number().min(1, 'Minimum spend/units must be greater than 0'),
    max: z.number().min(1, 'Maximum must be greater than or equal to 1'),
    description: z.string().optional(),
    learn_more_url: z.union([z.literal(''), z.string().trim().url()]),
    amount_off: z.number().min(0.01, 'Amount off must be greater than 0'),
    discount_type: z.string().min(1),
    period_type: z.string(),
    period_value: z.string(),
    period_year: z.string(),
    range: z.object({
      from: z.date(),
      to: z.date(),
    }),
    is_shared_with_network: z.boolean(),
    products: z
      .array(
        z.object({
          product_id: z
            .number()
            .min(1, 'A product must be selected')
            .nullable(),
          selectedVariants: z.record(z.string(), z.boolean()),
        })
      )
      .min(1, 'At least one product is required')
      .refine(
        (products) =>
          products.every(
            (p) =>
              p.product_id &&
              Object.values(p.selectedVariants).some((selected) => selected)
          ),
        'Each product must have at least one variant selected'
      ),
  })
  .refine((data) => data.max >= data.min, {
    message: 'Maximum must be greater than or equal to minimum',
    path: ['max'],
  })

type CreateRebateOfferForm = z.infer<typeof createRebateOfferFormSchema>

const currentYear = new Date().getFullYear()
const today = endOfToday()

const generatePeriodOptions = (year: number) => {
  const quarters: PeriodOption[] = [
    {
      label: `Q1 ${year}`,
      value: `1-${year}`,
      dates: {
        from: startOfQuarter(new Date(year, 0)),
        to: endOfQuarter(new Date(year, 0)),
      },
      isDisabled: false,
    },
    {
      label: `Q2 ${year}`,
      value: `2-${year}`,
      dates: {
        from: startOfQuarter(new Date(year, 3)),
        to: endOfQuarter(new Date(year, 3)),
      },
      isDisabled: false,
    },
    {
      label: `Q3 ${year}`,
      value: `3-${year}`,
      dates: {
        from: startOfQuarter(new Date(year, 6)),
        to: endOfQuarter(new Date(year, 6)),
      },
      isDisabled: false,
    },
    {
      label: `Q4 ${year}`,
      value: `4-${year}`,
      dates: {
        from: startOfQuarter(new Date(year, 9)),
        to: endOfQuarter(new Date(year, 9)),
      },
      isDisabled: false,
    },
  ].map((quarter) => ({
    ...quarter,
    isDisabled: isBefore(quarter.dates.to, today),
  }))

  const halves: PeriodOption[] = [
    {
      label: `H1 ${year}`,
      value: `1-${year}`,
      dates: {
        from: startOfYear(new Date(year, 0)),
        to: endOfQuarter(new Date(year, 5)),
      },
      isDisabled: false,
    },
    {
      label: `H2 ${year}`,
      value: `2-${year}`,
      dates: {
        from: startOfQuarter(new Date(year, 6)),
        to: endOfYear(new Date(year, 0)),
      },
      isDisabled: false,
    },
  ].map((half) => ({
    ...half,
    isDisabled: isBefore(half.dates.to, today),
  }))

  return { quarters, halves }
}

export function CreateRebateOfferModal({
  campaignId,
  onSuccess,
}: CreateRebateOfferModalProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const api = apiService()
  const { data: products, isLoading: isLoadingProducts } = api.useGetProducts()
  const { data: logos } = api.useGetCompanyLogos()
  const primaryLogo = useMemo(() => {
    return logos?.find((logo) => logo.is_primary)
  }, [logos])

  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<CreateRebateOfferForm>({
    resolver: zodResolver(createRebateOfferFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: RebateOfferType.TOTAL_VALUE,
      branding: RebateBranding.COMPANY,
      basis: 'volume',
      period_type: 'custom',
      period_value: '',
      description: '',
      learn_more_url: '',
      period_year: currentYear.toString(),
      range: {
        from: new Date(),
        to: addDays(new Date(), 20),
      },
      min: 1,
      max: 1,
      amount_off: 1,
      discount_type: 'dollars_off',
      is_shared_with_network: true,
      products: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  })

  const periodType = watch('period_type')
  const periodValue = watch('period_value')
  const periodYear = watch('period_year')
  const dateRange = watch('range')

  // Generate period options only once
  const periodOptions = useMemo<PeriodOptions>(
    () => ({
      [currentYear.toString()]: generatePeriodOptions(currentYear),
      [(currentYear + 1).toString()]: generatePeriodOptions(currentYear + 1),
    }),
    []
  )

  // Update dates when period selections change
  useEffect(() => {
    if (periodType !== 'custom' && periodValue && periodYear) {
      const options =
        periodType === 'quarter'
          ? periodOptions[periodYear]?.quarters
          : periodOptions[periodYear]?.halves
      const selectedPeriod = options?.find((opt) => opt.value === periodValue)
      if (selectedPeriod && !selectedPeriod.isDisabled) {
        setValue('range', {
          from: selectedPeriod.dates.from,
          to: selectedPeriod.dates.to,
        })
      }
    }
  }, [periodType, periodValue, periodYear, setValue, periodOptions])

  // Select first available period when year/type changes
  useEffect(() => {
    if (periodType !== 'custom' && periodYear) {
      const options =
        periodType === 'quarter'
          ? periodOptions[periodYear]?.quarters
          : periodOptions[periodYear]?.halves

      if (!options) return

      const currentSelection = options.find((opt) => opt.value === periodValue)
      if (!currentSelection || currentSelection.isDisabled) {
        const firstAvailable = options.find((opt) => !opt.isDisabled)
        if (firstAvailable) {
          setValue('period_value', firstAvailable.value)
          setValue('range', {
            from: firstAvailable.dates.from,
            to: firstAvailable.dates.to,
          })
        }
      }
    }
  }, [periodYear, periodType, periodValue, periodOptions, setValue])

  // Update discount_type when basis changes
  useEffect(() => {
    const basis = watch('basis')
    setValue(
      'discount_type',
      basis === 'volume' ? 'dollars_off' : 'percentage_off'
    )
  }, [watch('basis'), setValue])

  // Simplified variant initialization
  useEffect(() => {
    // Create a subscription to watch for product selection changes
    const subscription = watch((value, { name }) => {
      // Only trigger when a product_id field changes
      if (name && name.endsWith('.product_id')) {
        const index = parseInt(name.split('.')[1])
        const productId = value.products?.[index]?.product_id

        // Check if we have a valid product and it has variants
        if (productId && products) {
          const selectedProduct = products.find((p) => p.id === productId)

          if (selectedProduct?.variants?.length) {
            // Get current variants state
            const currentVariants =
              value.products?.[index]?.selectedVariants || {}

            // Clear any existing variants that don't belong to this product
            const cleanVariants: Record<string, boolean> = {}
            Object.entries(currentVariants).forEach(([key, value]) => {
              if (
                key.startsWith(`${productId}_`) &&
                typeof value === 'boolean'
              ) {
                cleanVariants[key] = value
              }
            })

            // Update form with cleaned variants first
            if (
              Object.keys(currentVariants).length !==
              Object.keys(cleanVariants).length
            ) {
              setValue(`products.${index}.selectedVariants`, cleanVariants, {
                shouldValidate: true,
              })
            }

            // Only initialize if no variants for this product are selected yet
            if (Object.keys(cleanVariants).length === 0) {
              // Default to selecting all variants with product-prefixed keys
              const initialVariants: Record<string, boolean> = {}
              selectedProduct.variants.forEach((v) => {
                if (v.id) initialVariants[`${productId}_${v.id}`] = true
              })

              // Update the form value
              setValue(`products.${index}.selectedVariants`, initialVariants, {
                shouldValidate: true,
              })
            }
          }
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [products, setValue, watch])

  /**
   * Transforms form data into format needed for both API and preview
   */
  function transformFormData(
    values: CreateRebateOfferForm,
    forApi = false
  ): CreateRebateOfferBody | any {
    // Extract product and variant data
    const productsData = values.products
      .map((product) => {
        // Skip if no product selected
        if (!product.product_id) return null

        // Find the selected product details
        const selectedProduct = products?.find(
          (p) => p.id === product.product_id
        )
        if (!selectedProduct) return null

        // Get the selected variant IDs (extract from product-prefixed keys)
        const selectedVariantIds = Object.entries(
          product.selectedVariants as Record<string, boolean>
        )
          .filter(([, isSelected]) => isSelected)
          .map(([key]) => {
            // Extract variant ID from "productId_variantId" format
            const parts = key.split('_')
            return parseInt(parts[parts.length - 1])
          })

        // Skip if no variants selected
        if (!selectedVariantIds.length) return null

        // For API calls, return simplified structure with just IDs
        if (forApi) {
          return {
            product_id: selectedProduct.id,
            product_variants: selectedVariantIds.map((id) => ({
              variant_id: id,
            })),
          } as CreateRebateOfferTermsProduct
        }

        // For preview, include full details
        const activeVariants = selectedProduct.variants?.filter(
          (v) => v.id && selectedVariantIds.includes(v.id)
        )

        return {
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          product_desc: '',
          product_variants: activeVariants?.map((v) => ({
            variant_id: v.id,
            variant_name: v.name,
            variant_format: v.format_pack_size,
            variant_gtin: v.gtin,
            variant_sku: v.sku,
            variant_desc: v.description,
            distributor_codes:
              v.distributor_codes?.map((code) => ({
                distributor_name: code.distributor_name,
                distributor_code: code.distributor_code,
              })) || [],
          })),
        }
      })
      .filter(Boolean)

    // Build common terms structure
    const terms = {
      tiers: [
        {
          min: values.min,
          max: values.max,
          discount: values.amount_off,
          discount_type: values.discount_type,
        },
      ],
      basis: values.basis,
      period_type: values.period_type,
      period_value: values.period_value,
      period_year: values.period_year,
      products: productsData,
    }

    // Build the response object once
    const result = {
      name: values.name,
      description: values.description,
      learn_more_url: values.learn_more_url,
      valid_from: values?.range?.from,
      valid_to: values?.range?.to,
      campaign_id: campaignId,
      type: values.type,
      branding: values.branding,
      is_shared_with_network: values.is_shared_with_network,
      terms: terms,
    }

    // Conditionally apply type cast based on forApi parameter
    return forApi ? (result as CreateRebateOfferBody) : result
  }

  // Handle form submission
  const onSubmit = handleSubmit(async (values) => {
    // Get API submission data
    const body = transformFormData(values, true) as CreateRebateOfferBody

    // Validate product selection
    if (!body.terms.products.length) {
      toast.error('At least one product with variants must be selected')
      return
    }

    // Submit the form
    setIsSubmitting(true)
    const [err, response] = await to(api.createRebateOffer(body))
    setIsSubmitting(false)

    if (err) {
      console.error(err)
      toast.error('Failed to create rebate offer')
      return
    }

    const offerId = (response as any)?.id
    if (!offerId) {
      toast.error('Failed to get offer ID')
      return
    }

    toast.success('Rebate offer created successfully')
    queryClient.invalidateQueries({
      queryKey: [REBATE_OFFERS_TABLE_KEY],
      exact: false,
    })

    reset()
    setOpen(false)
    onSuccess?.()
    navigate(`/rebates/offer/${offerId}/details?new=true`)
  })

  // Update preview text based on form values
  const basis = watch('basis')
  const min = watch('min')
  const max = watch('max')
  const amountOff = watch('amount_off')
  const offerType = watch('type')
  const branding = watch('branding')
  const companyName = user?.company?.name || 'your company'

  // Extract products to a variable so it can be properly watched for preview updates
  const formProducts = watch('products')

  // Get the first selected product's name - memoized to properly track formProducts changes
  const getFirstProductName = useCallback(() => {
    const firstProductId = formProducts?.[0]?.product_id
    if (!firstProductId || !products) return 'Product'
    const product = products.find((p) => p.id === firstProductId)
    return product?.name || 'Product'
  }, [formProducts, products])

  // Check if multiple products are selected
  const hasMultipleProducts = () => {
    const productSelections = watch('products')
    const selectedProducts = productSelections.filter(
      (p) => p.product_id !== null
    )
    return selectedProducts.length > 1
  }

  // Watch for changes in product selection to update branding if needed
  useEffect(() => {
    const productSelections = watch('products')
    const selectedProducts = productSelections.filter(
      (p) => p.product_id !== null
    )
    const currentBranding = watch('branding')

    // If multiple products are selected and current branding is single-product, switch to COMPANY
    if (selectedProducts.length > 1) {
      if (
        currentBranding === RebateBranding.PRODUCT ||
        currentBranding === RebateBranding.PRODUCT_FROM_COMPANY
      ) {
        setValue('branding', RebateBranding.COMPANY)
      }
    }
  }, [watch('products')])

  // Handle variant selection from modal
  // Convert form data to ProductVariantSelector format
  const formDataToSelections = useMemo((): ProductWithVariants[] => {
    return fields.map((field, index) => {
      const productId = formProducts?.[index]?.product_id
      const selectedVariantRecord =
        formProducts?.[index]?.selectedVariants || {}

      const selectedProduct = findProductById(products, productId)
      if (!selectedProduct) {
        return {
          productId: productId || 0,
          productName: '',
          variantIds: [],
          variants: [],
        }
      }

      const selectedVariantKeys = Object.keys(selectedVariantRecord).filter(
        (key) => selectedVariantRecord[key]
      )

      const selectedVariants =
        selectedProduct?.variants?.filter((v) =>
          selectedVariantKeys.some((key) => key.endsWith(`_${v.id}`))
        ) || []

      return {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        variantIds: selectedVariants
          .map((v) => v.id)
          .filter((id): id is number => id !== undefined),
        variants: selectedVariants.map(mapVariantToDisplay),
      }
    })
  }, [fields, products, formProducts])

  // Handlers for ProductVariantSelector
  const handleAddProduct = (selection: ProductWithVariants) => {
    const selectedVariants = buildSelectedVariantsRecord(
      selection.productId,
      selection.variantIds
    )

    append({
      product_id: selection.productId,
      selectedVariants,
    })
  }

  const handleEditProduct = (index: number, selection: ProductWithVariants) => {
    const selectedVariants = buildSelectedVariantsRecord(
      selection.productId,
      selection.variantIds
    )

    // Update the entire product object to ensure watch() detects the change
    const currentProducts = getValues('products')
    const updatedProducts = [...currentProducts]
    updatedProducts[index] = {
      ...updatedProducts[index],
      product_id: selection.productId,
      selectedVariants,
    }

    setValue('products', updatedProducts, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  const handleRemoveProduct = (index: number) => {
    remove(index)
  }

  const getBrandingText = () => {
    switch (branding) {
      case RebateBranding.COMPANY:
        return companyName
      case RebateBranding.COMPANY_PRODUCTS:
        return `${companyName} products`
      case RebateBranding.SELECT_COMPANY_PRODUCTS:
        return `select ${companyName} products`
      case RebateBranding.PRODUCT:
        return getFirstProductName()
      case RebateBranding.PRODUCT_FROM_COMPANY:
        return `${getFirstProductName()} from ${companyName}`
      default:
        return companyName
    }
  }

  const previewText = useMemo(() => {
    const brandingText = getBrandingText()
    const total = amountOff * max

    switch (offerType) {
      case RebateOfferType.TOTAL_VALUE:
        return `Up to $${total} cash back on your first ${max} ${max === 1 ? 'case' : 'cases'} of ${brandingText}`

      case RebateOfferType.SINGLE_CASE:
        return `$${amountOff} cash back on your first case of ${brandingText}`

      case RebateOfferType.PRICE_PER_CASE:
        return `$${amountOff} off per case of ${brandingText}, up to ${max} ${max === 1 ? 'case' : 'cases'}`

      case RebateOfferType.MINIMUM_ORDER:
        return `$${amountOff} off per case of ${brandingText}, when you buy at least ${min} ${min === 1 ? 'case' : 'cases'}`

      // Legacy support
      case RebateOfferType.FIRST_OFFER:
        return `$${amountOff} off your first ${max === 1 ? 'case' : `${max} cases`} of ${brandingText} at every one of your locations`

      case RebateOfferType.TIME_OFFER:
        return `$${amountOff} off per case of ${brandingText} at every one of your locations`

      default:
        return `$${amountOff} off per case of ${brandingText}`
    }
  }, [
    offerType,
    branding,
    min,
    max,
    amountOff,
    companyName,
    products,
    formProducts,
    getFirstProductName,
  ])

  // Memoized preview data - only updates when fields that affect visual preview change
  // Text fields (name, description) don't cause re-renders
  const preview = useMemo(() => {
    const currentValues = getValues()
    return transformFormData(currentValues, false)
  }, [
    formProducts,
    offerType,
    branding,
    min,
    max,
    amountOff,
    basis,
    dateRange,
    periodType,
    periodValue,
    periodYear,
    products,
  ])

  // Check if preview has minimum required data to display
  // Note: We don't check name here to avoid re-renders on every keystroke
  const isPreviewReady = useMemo(() => {
    const hasProduct = formProducts?.some(
      (p) =>
        p.product_id &&
        Object.values(p.selectedVariants || {}).some((selected) => selected)
    )
    const hasAmount = amountOff > 0
    return hasProduct && hasAmount
  }, [formProducts, amountOff])

  // Defer preview data to prevent flickering during rapid updates
  const deferredPreview = useDeferredValue(preview)
  const deferredPreviewText = useDeferredValue(previewText)

  // Memoize the data object for OfferPage to prevent re-renders from unrelated state changes
  const offerPageData = useMemo(
    () => ({
      firstbite_id: '',
      offer: {
        ...deferredPreview,
        manufacturer_details: {
          name: user?.company?.name,
          logo: new URL(primaryLogo?.logo_url ?? '', API_HOST).href,
        },
      },
    }),
    [deferredPreview, user?.company?.name, primaryLogo?.logo_url]
  )

  const [selectedViewport, setSelectedViewport] =
    useState<ViewportPreset>('iPhone')

  return (
    <Fragment>
      <FbButton onClick={() => setOpen(true)}>Create Rebate Offer</FbButton>
      <Modal
        size="2xl"
        smallButton={true}
        open={open}
        onOpenChange={(open) => {
          if (!open) {
            reset()
            setOpen(open)
          }
        }}
        loading={isSubmitting || isLoadingProducts}
        blockAccept={!isValid}
        title="Create Rebate Offer"
        onAccept={onSubmit}
        acceptButtonText="Create Offer"
        headerExtra={
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Preview Size: </span>
            <select
              value={selectedViewport}
              onChange={(e) =>
                setSelectedViewport(e.target.value as ViewportPreset)
              }
              className="text-sm py-1 w-30 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(VIEWPORT_PRESETS).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.label}
                </option>
              ))}
            </select>
          </div>
        }
      >
        <div className="flex flex-row gap-2 ">
          <div className="flex-1 flex-shrink-0 h-[550px] overflow-y-auto pr-6">
            <TextInput
              control={control}
              name="name"
              label="Name of the offer"
              helperText={errors.name?.message}
              secondaryLabel=" - external"
            />

            <TextareaInput
              control={control}
              name="description"
              label="Description of the offer"
              maxLength={150}
              secondaryLabel=" - internal"
            />

            <TextInput
              control={control}
              name="learn_more_url"
              label="Learn More URL"
              helperText={errors.learn_more_url?.message}
              optional
            />

            <div className="space-y-4 mt-4">
              <div className="mb-2">
                <h3 className="text-base font-medium mb-3">
                  Products & Variants
                </h3>

                <ProductVariantSelector
                  products={products}
                  isLoadingProducts={isLoadingProducts}
                  selections={formDataToSelections}
                  onAdd={handleAddProduct}
                  onEdit={handleEditProduct}
                  onRemove={handleRemoveProduct}
                  multipleProducts={true}
                  multipleVariants={true}
                  showVariantList={true}
                />
              </div>
            </div>

            <Select
              control={control}
              name="type"
              label="Rebate Type"
              options={[
                {
                  label: 'Total Value',
                  value: RebateOfferType.TOTAL_VALUE,
                },
                {
                  label: 'Single Case Offer',
                  value: RebateOfferType.SINGLE_CASE,
                },
                {
                  label: 'Price per Case',
                  value: RebateOfferType.PRICE_PER_CASE,
                },
                {
                  label: 'Minimum Order Required',
                  value: RebateOfferType.MINIMUM_ORDER,
                },
              ]}
            />

            <div className="space-y-2 mt-3">
              <label className="text-base font-medium text-gray-800 select-none">
                Rebate Branding
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  {
                    value: RebateBranding.COMPANY,
                    label: companyName || 'Company',
                  },
                  {
                    value: RebateBranding.COMPANY_PRODUCTS,
                    label: `${companyName || 'Company'} Products`,
                  },
                  {
                    value: RebateBranding.SELECT_COMPANY_PRODUCTS,
                    label: `Select ${companyName || 'Company'} Products`,
                  },
                  {
                    value: RebateBranding.PRODUCT,
                    label: getFirstProductName(),
                    disabled: hasMultipleProducts(),
                  },
                  {
                    value: RebateBranding.PRODUCT_FROM_COMPANY,
                    label: `${getFirstProductName()} from ${companyName || 'Company'}`,
                    disabled: hasMultipleProducts(),
                  },
                ].map((option) => {
                  const button = (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() =>
                        !option.disabled && setValue('branding', option.value)
                      }
                      disabled={option.disabled}
                      className={`
                        flex items-center space-x-3 p-3 rounded-lg border-2 transition-all w-full
                        ${
                          watch('branding') === option.value
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }
                        ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                      `}
                    >
                      <div className="relative flex items-center justify-center">
                        <div
                          className={`
                            w-4 h-4 rounded-full border-2 transition-all
                            ${
                              watch('branding') === option.value
                                ? 'border-emerald-500'
                                : 'border-gray-300'
                            }
                          `}
                        >
                          {watch('branding') === option.value && (
                            <div className="absolute inset-0 m-0.5 bg-emerald-500 rounded-full" />
                          )}
                        </div>
                      </div>
                      <span
                        className={`text-sm ${option.disabled ? 'text-gray-400' : 'text-gray-700'}`}
                      >
                        {option.label}
                      </span>
                    </button>
                  )

                  // Wrap disabled buttons with tooltip
                  if (option.disabled) {
                    return (
                      <Tooltip
                        key={option.value}
                        content="Only available when a single product is selected"
                        placement="top"
                        delay={[200, 0]}
                      >
                        {button}
                      </Tooltip>
                    )
                  }

                  return button
                })}
              </div>
            </div>

            <div className="space-y-4">
              {/* Period Selection Row */}
              <div>
                <RadioButtonSelect
                  horizontal={false}
                  control={control}
                  name="period_type"
                  label="Period Type"
                  options={[
                    {
                      label: 'Quarter',
                      key: 'quarter',
                      icon: Calendar,
                    },
                    {
                      label: 'Half',
                      key: 'half',
                      icon: CalendarRange,
                    },
                    {
                      label: 'Custom Range',
                      key: 'custom',
                      icon: CalendarDays,
                    },
                  ]}
                />
              </div>

              {periodType !== 'custom' && (
                <div className="flex gap-4">
                  <Select
                    control={control}
                    name="period_year"
                    label="Select Year"
                    options={[
                      {
                        label: currentYear.toString(),
                        value: currentYear.toString(),
                      },
                      {
                        label: (currentYear + 1).toString(),
                        value: (currentYear + 1).toString(),
                      },
                    ]}
                  />
                  <Select
                    control={control}
                    name="period_value"
                    label={`Select ${periodType === 'quarter' ? 'Quarter' : 'Half'}`}
                    options={
                      periodType === 'quarter'
                        ? periodOptions[periodYear]?.quarters
                        : periodOptions[periodYear]?.halves
                    }
                  />
                </div>
              )}

              {periodType === 'custom' && (
                <DateRangePicker
                  label="Period"
                  control={control}
                  name="range"
                />
              )}

              {/* Minimum and Maximum Row */}
              <div className="flex gap-4">
                <div className="flex-1">
                  <TextInput
                    type="number"
                    control={control}
                    name="min"
                    label={
                      basis === 'volume' ? 'Minimum Cases' : 'Minimum Spend'
                    }
                    prefix={basis === 'volume' ? undefined : '$'}
                    preventNegative={true}
                    helperText={
                      basis === 'volume'
                        ? 'Minimum threshold to qualify for discount'
                        : undefined
                    }
                  />
                </div>
                <div className="flex-1">
                  <TextInput
                    type="number"
                    control={control}
                    name="max"
                    label={
                      basis === 'volume' ? 'Maximum Cases' : 'Maximum Spend'
                    }
                    prefix={basis === 'volume' ? undefined : '$'}
                    preventNegative={true}
                    helperText={
                      basis === 'volume'
                        ? 'Maximum cases eligible for discount'
                        : undefined
                    }
                  />
                </div>
              </div>

              {/* Amount Off Row */}
              <div className="flex-1">
                <TextInput
                  type="number"
                  control={control}
                  name="amount_off"
                  label={
                    basis === 'volume'
                      ? 'Amount Off per Case'
                      : 'Percentage Off Order'
                  }
                  prefix={basis === 'volume' ? '$' : undefined}
                  suffix={basis === 'volume' ? undefined : '%'}
                  preventNegative={true}
                />
              </div>
            </div>

            <div className="mt-4">
              <Checkbox control={control} name="is_shared_with_network">
                <Tooltip
                  content="By sharing this rebate with First Bite's Rebate Network, you give First Bite permission to share, promote, and repurpose this rebate. If left unchecked, it remains private and only available to those you share it with."
                  placement="right"
                  delay={[200, 0]}
                >
                  <div className="flex items-center gap-2 cursor-help">
                    Share offer with First Bite Rebate Network
                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 text-base" />
                  </div>
                </Tooltip>
              </Checkbox>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <h3 className="text-lg font-medium mb-2">Offer Preview</h3>
              <p>{deferredPreviewText}</p>
            </div>
          </div>

          <div className="flex-shrink-0 h-[550px] overflow-hidden">
            {isPreviewReady ? (
              <MobilePreview selectedViewport={selectedViewport}>
                <OfferPage
                  isPreview={true}
                  isExpired={false}
                  isPaused={false}
                  data={offerPageData as any}
                  onClaimOffer={() => {}}
                />
              </MobilePreview>
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-center text-gray-500 p-6">
                  <p className="text-lg font-medium mb-2">Preview</p>
                  <p className="text-sm">
                    Select a product and set the rebate amount
                    <br />
                    to see preview
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Fragment>
  )
}
